import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  Alert,
  GetSubscriptionResponse,
  PostAlertsRequest,
  PostAlertsResponse,
  PostSubscriptionRequest,
  SourceType,
} from '@toggle/toggle';

// TODO: swap back to real services once endpoints are available
// import {
//   getSubscription,
//   postAlerts,
//   postRead,
//   postReadAll,
//   postSubscription,
// } from '~/services/event-alerts/event-alerts-service';
import {
  getSubscription,
  postAlerts,
  postRead,
  postReadAll,
  postSubscription,
} from './mock-data/mock-services';
import {
  optimisticQueriesUpdate,
  optimisticQueryUpdate,
} from '~/utils/optimistic-updates/optimistic-updates';

export const QUERY_KEY_EVENT_ALERTS = 'QUERY_KEY_EVENT_ALERTS';
export const QUERY_KEY_EVENT_ALERT_SUBSCRIPTION =
  'QUERY_KEY_EVENT_ALERT_SUBSCRIPTION';

type EventAlertsPaginatedResponse = {
  pages: PostAlertsResponse[];
  pageParams: (string | null)[];
};

// TODO: we will need to invalidate this query when the websocket gives us an update
export const usePostAlerts = (body: PostAlertsRequest) =>
  useInfiniteQuery({
    queryKey: [QUERY_KEY_EVENT_ALERTS, body],
    queryFn: ({ pageParam }) =>
      postAlerts({ ...body, next: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage): string | null =>
      lastPage?.next_cursor ?? null,
    staleTime: getTimeTillEndOfDayMs(),
  });

export const useGetSubscription = (sourceId: string, sourceType: SourceType) =>
  useQuery({
    queryKey: [QUERY_KEY_EVENT_ALERT_SUBSCRIPTION, sourceId, sourceType],
    queryFn: () => getSubscription(sourceId, sourceType),
  });

// When we update the subscription state of an alert, we need to optimistically update the cached subscription
// and alerts data on the FE. Once the network call is complete, we force a refetch on the getSubscription query
export const usePostSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PostSubscriptionRequest) => postSubscription(body),
    onMutate: async (body: PostSubscriptionRequest) => {
      const newStatus = body.track ? 'active' : 'stopped';

      // Optimistically update the cached data from the getSubscription query
      const alertSubscriptionQueryKey = [
        QUERY_KEY_EVENT_ALERT_SUBSCRIPTION,
        body.source_id,
        body.source_type,
      ];

      const { previousData: previousSubscriptionQuery } =
        await optimisticQueryUpdate<GetSubscriptionResponse>({
          queryClient,
          queryKey: alertSubscriptionQueryKey,
          updateFn: oldData => ({
            ...oldData,
            tracking_status: newStatus,
          }),
        });

      // Optimistically update the cached data from the postAlerts query
      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: oldData => ({
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              alerts: page.alerts.map((alert: Alert) =>
                alert.source_id === body.source_id &&
                alert.source_type === body.source_type
                  ? { ...alert, tracking_status: newStatus }
                  : alert
              ),
            })),
          }),
        });

      return {
        previousAlertQueries,
        previousSubscriptionQuery,
        alertSubscriptionQueryKey,
      };
    },
    onError: (_err, _body, context) => {
      if (context?.previousSubscriptionQuery) {
        queryClient.setQueryData(
          context.alertSubscriptionQueryKey,
          context.previousSubscriptionQuery
        );
      }

      if (context?.previousAlertQueries) {
        context.previousAlertQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      // Force a refetch on the getSubscription query
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEY_EVENT_ALERT_SUBSCRIPTION,
          variables.source_id,
          variables.source_type,
        ],
      });
    },
  });
};

export const usePostRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => postRead({ ids }),
    onMutate: async (ids: string[]) => {
      const idSet = new Set(ids);

      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: oldData => ({
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              alerts: page.alerts.map((alert: Alert) =>
                idSet.has(alert.id) ? { ...alert, read: !alert.read } : alert
              ),
            })),
          }),
        });

      return { previousAlertQueries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_EVENT_ALERTS],
        refetchType: 'none',
      });
    },
    onError: (_err, _ids, context) => {
      if (context?.previousAlertQueries) {
        context.previousAlertQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};

export const usePostReadAll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => postReadAll(),
    onMutate: async () => {
      const { previousData: previousAlertQueries } =
        await optimisticQueriesUpdate<EventAlertsPaginatedResponse>({
          queryClient,
          queryKey: [QUERY_KEY_EVENT_ALERTS],
          updateFn: oldData => ({
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              alerts: page.alerts.map((alert: Alert) => ({
                ...alert,
                read: true,
              })),
            })),
          }),
        });

      return {
        previousAlertQueries,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_EVENT_ALERTS],
        refetchType: 'none',
      });
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAlertQueries) {
        context.previousAlertQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
};
